// components/AI_Chat/ChatInput.tsx — barre de saisie alignée sur l'app mobile (_input2 de kuma_chat_screen.dart)
import { motion } from "framer-motion";
import { ReactComponent as IconAdd } from "assets/img/icons/icon_attachment.svg";
import { ReactComponent as IconMessage } from "assets/img/icons/message.svg";
import { ReactComponent as IconTool } from "assets/img/icons/settings.svg";
import { useEffect, useRef } from "react";
import React from "react";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onFileUpload: (file: File) => void;
  onToolSelect: () => void;
  isTyping?: boolean;
  disabled?: boolean;
}

const ChatInput = ({ value, onChange, onSend, onFileUpload, onToolSelect, isTyping, disabled }: ChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  };

  useEffect(() => { autoResize(); }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileUpload(file);
  };

  const isDisabled = disabled || isTyping;

  return (
    <motion.div
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="chat-input-modern"
    >
      <div className="input-row">
        <input type="file" id="file-upload" hidden onChange={handleFileChange} />
        <motion.label
          htmlFor="file-upload"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.93 }}
          className="action-btn"
          title="Joindre un fichier"
        >
          <IconAdd width={16} height={16} fill="currentColor" />
        </motion.label>

        <motion.button
          type="button"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.93 }}
          className="action-btn"
          title="Choisir un outil"
          onClick={onToolSelect}
        >
          <IconTool width={16} height={16} />
        </motion.button>

        <div className="input-wrapper">
          <span className="field-icon">
            <IconMessage />
          </span>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Écrivez à Kuma…"
            rows={1}
            disabled={isDisabled}
            className="input-field"
          />
        </div>

        <motion.button
          type="button"
          whileTap={{ scale: 0.9 }}
          className="send-btn"
          onClick={() => onSend()}
          disabled={isDisabled || !value.trim()}
          title="Envoyer"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.6 1.4a1.4 1.4 0 0 0-1.47-.32L1.6 8.36a1.4 1.4 0 0 0-.1 2.59l8.4 3.76 3.76 8.4a1.4 1.4 0 0 0 2.59-.1l7.28-19.53A1.4 1.4 0 0 0 22.6 1.4ZM10.6 12.9 3.9 9.9l14.14-5.27L10.6 12.9Zm3.5 7.2-3-6.7 8.27-8.27L14.1 20.1Z" />
          </svg>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ChatInput;
